import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Swal from "sweetalert2";
import axios from "axios";
import {
  FaPlus,
  FaFilter,
  FaEdit,
  FaTrash,
  FaEye,
  FaSearch,
  FaClipboardList,
  FaPills
} from "react-icons/fa";
import {
  FiChevronLeft,
  FiChevronRight,
  FiUploadCloud,
  FiBox,
  FiCheckCircle,
  FiLayers
} from "react-icons/fi";
import FilterModel from "./FilterModel";

const ObatPage = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [obatList, setObatList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState({
    per_page: 10,
    page: 1,
    order_by: "name",
    status: "all",
    search: "",
  });
  const [totalPages, setTotalPages] = useState(1);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [totalData, setTotalData] = useState(0);

  const token = localStorage.getItem("token") || sessionStorage.getItem("token");
  const userRole = (localStorage.getItem("role") || "").toLowerCase();
  const id_outlet = localStorage.getItem("id_outlet");

  const fetchObat = async () => {
    if (!token) return;
    const params = new URLSearchParams({
      per_page: appliedFilters.per_page,
      page: appliedFilters.page,
      order_by: appliedFilters.order_by,
      search: appliedFilters.search,
    });

    let url = `${process.env.REACT_APP_API_BASE_URL}/v1/drug`;
    if (userRole === "manager") {
      url += `?id_outlet=${id_outlet}&${params.toString()}`;
    } else {
      url += `?${params.toString()}`;
    }

    setLoading(true);
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.message || "Gagal mengambil data obat");

      setObatList(result.data || []);
      const { current_page, last_page, total } = result.meta || {};
      setCurrentPage(current_page || 1);
      setTotalPages(last_page || 1);
      setTotalData(total || 0);
    } catch (error) {
      console.error(error);
      Swal.fire({
        icon: "error",
        title: "Gagal Memuat Obat",
        text: "Terjadi kesalahan saat mengambil daftar obat.",
        customClass: { popup: "rounded-3xl" },
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchObat();
  }, [appliedFilters]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setAppliedFilters((prev) => ({ ...prev, page: newPage }));
      setCurrentPage(newPage);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/v1/drug/import`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.message || "Gagal mengimpor data obat");

      await Swal.fire({
        title: "Import Berhasil!",
        text: "Data obat dari file Excel berhasil diimpor.",
        icon: "success",
        timer: 1800,
        showConfirmButton: false,
        customClass: { popup: "rounded-3xl" },
      });

      fetchObat();
    } catch (error) {
      console.error(error);
      Swal.fire({
        title: "Gagal Mengimpor",
        text: error.message || "Terjadi kesalahan saat mengimpor data obat.",
        icon: "error",
        customClass: { popup: "rounded-3xl" },
      });
    }
  };

  const handleSearchChange = (e) => {
    setAppliedFilters((prev) => ({ ...prev, search: e.target.value, page: 1 }));
  };

  const handleApplyFilters = (filters) => {
    setAppliedFilters(filters);
    setIsFilterOpen(false);
  };

  const handleDelete = (id, name) => {
    Swal.fire({
      title: `Hapus obat "${name}"?`,
      text: "Data obat akan dihapus secara permanen dari katalog!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#EF4444",
      cancelButtonColor: "#6B7280",
      confirmButtonText: "Ya, Hapus!",
      cancelButtonText: "Batal",
      customClass: { popup: "rounded-3xl" },
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/v1/drug/delete/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
          });

          await Swal.fire({
            title: "Terhapus!",
            text: `Obat ${name} berhasil dihapus.`,
            icon: "success",
            timer: 1800,
            showConfirmButton: false,
            customClass: { popup: "rounded-3xl" },
          });

          fetchObat();
        } catch (err) {
          console.error(err);
          Swal.fire({
            title: "Gagal Menghapus",
            text: err.response?.data?.message || "Terjadi kesalahan server.",
            icon: "error",
            customClass: { popup: "rounded-3xl" },
          });
        }
      }
    });
  };

  const totalStockSum = obatList.reduce((sum, item) => sum + (item.stock || 0), 0);
  const isAllowedToManage = ["owner", "admin", "superadmin"].includes(userRole);

  return (
    <div className="space-y-6 pb-12">
      {/* 1. Metric Summary Cards */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="grid grid-cols-1 sm:grid-cols-3 gap-5"
      >
        <div className="bg-white rounded-3xl p-5 shadow-xl shadow-gray-200/50 border border-gray-100 flex items-center justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-1">
              Total Obat (Master)
            </p>
            <h2 className="text-3xl font-extrabold text-gray-900">
              {loading ? "..." : totalData}
            </h2>
          </div>
          <div className="p-3.5 rounded-2xl bg-emerald-50 text-emerald-600 border border-emerald-100">
            <FaPills className="w-6 h-6" />
          </div>
        </div>

        <div className="bg-white rounded-3xl p-5 shadow-xl shadow-gray-200/50 border border-gray-100 flex items-center justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-1">
              Total Stok Halaman Ini
            </p>
            <h2 className="text-3xl font-extrabold text-teal-600">
              {loading ? "..." : totalStockSum}
            </h2>
          </div>
          <div className="p-3.5 rounded-2xl bg-teal-50 text-teal-600 border border-teal-100">
            <FiBox className="w-6 h-6" />
          </div>
        </div>

        <div className="bg-white rounded-3xl p-5 shadow-xl shadow-gray-200/50 border border-gray-100 flex items-center justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-1">
              Tipe Filter Aktif
            </p>
            <h2 className="text-2xl font-extrabold text-emerald-700 capitalize">
              {appliedFilters.order_by}
            </h2>
          </div>
          <div className="p-3.5 rounded-2xl bg-emerald-50 text-emerald-600 border border-emerald-100">
            <FiLayers className="w-6 h-6" />
          </div>
        </div>
      </motion.div>

      {/* 2. Main Control Bar & Table Container */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.1 }}
        className="bg-white p-6 rounded-3xl shadow-xl shadow-gray-200/50 border border-gray-100 space-y-6"
      >
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          {/* Live Search */}
          <div className="relative w-full md:w-80">
            <FaSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Cari nama obat, dosis, produsen..."
              value={appliedFilters.search}
              onChange={handleSearchChange}
              className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all font-semibold"
            />
          </div>

          <div className="flex flex-wrap items-center gap-3 w-full md:w-auto justify-end">
            <button
              onClick={() => setIsFilterOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 text-xs font-semibold rounded-2xl transition-all shadow-sm"
            >
              <FaFilter className="w-3.5 h-3.5 text-emerald-600" />
              <span>Filter</span>
            </button>

            <Link to="/obat-paket/obat/register">
              <button className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 text-xs font-semibold rounded-2xl transition-all shadow-sm">
                <FaClipboardList className="w-3.5 h-3.5 text-emerald-600" />
                <span>Register Obat</span>
              </button>
            </Link>

            {isAllowedToManage && (
              <label className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 text-xs font-semibold rounded-2xl transition-all shadow-sm cursor-pointer">
                <FiUploadCloud className="w-4 h-4 text-emerald-600" />
                <span>Import Excel</span>
                <input type="file" accept=".xlsx, .xls" onChange={handleFileUpload} className="hidden" />
              </label>
            )}

            {/* Create Obat CTA (GREEN BUTTON) */}
            {isAllowedToManage && (
              <Link to="/obat-paket/obat/create">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white text-xs font-bold rounded-2xl shadow-lg shadow-emerald-600/20 transition-all"
                >
                  <FaPlus className="w-3.5 h-3.5" />
                  <span>Buat Obat Baru</span>
                </motion.button>
              </Link>
            )}
          </div>
        </div>

        {/* Table Content */}
        {loading ? (
          <div className="flex justify-center items-center py-16">
            <div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="overflow-x-auto rounded-2xl border border-gray-100 shadow-sm">
            <table className="w-full text-left text-xs text-gray-600">
              <thead>
                <tr className="bg-gray-50 text-gray-400 font-bold uppercase tracking-wider text-[10px]">
                  <th className="p-3.5 w-12 text-center">No</th>
                  <th className="p-3.5">Nama Obat</th>
                  <th className="p-3.5">Produsen</th>
                  <th className="p-3.5">Dosis</th>
                  <th className="p-3.5 text-center">Total Stok</th>
                  <th className="p-3.5">Harga Beli</th>
                  <th className="p-3.5">Harga Jual</th>
                  <th className="p-3.5 text-center">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {obatList.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="p-12 text-center text-gray-400 font-semibold">
                      Belum ada data obat ditemukan
                    </td>
                  </tr>
                ) : (
                  obatList.map((item, index) => (
                    <tr key={item.id} className="hover:bg-emerald-50/20 transition-colors">
                      <td className="p-3.5 text-center font-bold text-gray-400">
                        {(currentPage - 1) * appliedFilters.per_page + index + 1}
                      </td>
                      <td className="p-3.5 font-extrabold text-gray-900 text-sm">
                        {item.name}
                      </td>
                      <td className="p-3.5 font-semibold text-gray-700">{item.produsen || "-"}</td>
                      <td className="p-3.5 font-semibold text-gray-600">{item.dosis || "-"}</td>
                      <td className="p-3.5 text-center">
                        <span
                          className={`inline-flex px-3 py-1 rounded-full text-xs font-mono font-bold ${
                            item.stock > 10
                              ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                              : item.stock > 0
                              ? "bg-amber-50 text-amber-700 border border-amber-200"
                              : "bg-rose-50 text-rose-700 border border-rose-200"
                          }`}
                        >
                          {item.stock || 0}
                        </span>
                      </td>
                      <td className="p-3.5 font-mono font-bold text-gray-700">
                        Rp {(item.harga_beli || 0).toLocaleString("id-ID")}
                      </td>
                      <td className="p-3.5 font-mono font-extrabold text-emerald-700">
                        Rp {(item.harga_jual || 0).toLocaleString("id-ID")}
                      </td>
                      <td className="p-3.5 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <Link
                            to={`/obat-paket/obat/detail/${encodeURI(item.name + "_" + item.dosis + "_" + item.produsen)}`}
                            className="p-2 rounded-xl bg-emerald-50 hover:bg-emerald-100 text-emerald-700 transition-all"
                            title="Lihat Detail & Kartu Stok"
                          >
                            <FaEye className="w-3.5 h-3.5" />
                          </Link>

                          {isAllowedToManage && (
                            <>
                              <Link
                                to={`/obat-paket/obat/edit/${encodeURI(item.name + "_" + item.dosis + "_" + item.produsen)}`}
                                className="p-2 rounded-xl bg-amber-50 hover:bg-amber-100 text-amber-700 transition-all"
                                title="Edit Obat"
                              >
                                <FaEdit className="w-3.5 h-3.5" />
                              </Link>
                              <button
                                onClick={() => handleDelete(item.id, item.name)}
                                className="p-2 rounded-xl bg-rose-50 text-rose-600 hover:bg-rose-100 transition-all"
                                title="Hapus Obat"
                              >
                                <FaTrash className="w-3.5 h-3.5" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Controls */}
        <div className="flex flex-col sm:flex-row items-center justify-between pt-4 border-t border-gray-100 gap-3 text-xs">
          <p className="text-gray-400">
            Menampilkan <span className="font-bold text-gray-700">{obatList.length}</span> dari{" "}
            <span className="font-bold text-gray-700">{totalData}</span> obat
          </p>

          <div className="flex items-center gap-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage <= 1}
              className={`p-2 rounded-xl border flex items-center gap-1 font-semibold transition-all ${
                currentPage <= 1
                  ? "bg-gray-100 text-gray-300 border-gray-200 cursor-not-allowed"
                  : "bg-white text-gray-700 border-gray-200 hover:bg-emerald-50 hover:text-emerald-600 hover:border-emerald-200"
              }`}
            >
              <FiChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-xs font-semibold text-gray-600 px-2">
              Halaman {currentPage} dari {totalPages}
            </span>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage >= totalPages}
              className={`p-2 rounded-xl border flex items-center gap-1 font-semibold transition-all ${
                currentPage >= totalPages
                  ? "bg-gray-100 text-gray-300 border-gray-200 cursor-not-allowed"
                  : "bg-white text-gray-700 border-gray-200 hover:bg-emerald-50 hover:text-emerald-600 hover:border-emerald-200"
              }`}
            >
              <FiChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </motion.div>

      {/* Filter Modal */}
      <FilterModel
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        onApply={handleApplyFilters}
      />
    </div>
  );
};

export default ObatPage;
